import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/cn';

/** Anchored panel that closes on outside click and Escape. */
export function Popover({
  trigger,
  children,
  className,
}: {
  readonly trigger: (props: { readonly open: boolean; readonly toggle: () => void }) => ReactNode;
  readonly children: (close: () => void) => ReactNode;
  readonly className?: string;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onPointer = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && setOpen(false);
    document.addEventListener('pointerdown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);
  return (
    <div ref={root} className="relative">
      {trigger({ open, toggle: () => setOpen((value) => !value) })}
      {open && (
        <div
          role="dialog"
          className={cn(
            'absolute top-full right-0 z-40 mt-2 max-h-[min(72vh,680px)] w-[380px] origin-top-right overflow-y-auto rounded-2xl bg-panel p-2 shadow-float ring-1 ring-line',
            // Phones: pin to the screen edges instead of the trigger, so it never overflows.
            'max-sm:fixed max-sm:inset-x-4 max-sm:top-[4.5rem] max-sm:mt-0 max-sm:w-auto',
            className,
          )}
        >
          {children(() => setOpen(false))}
        </div>
      )}
    </div>
  );
}
